<script lang="ts">
  // Recent Activity feed per INTERFACE-SPECIFICATION.md §3.2 — last
  // governance events across all products. The manifest stores
  // every version with its proposer, approver, and effective-from
  // metadata; this component flattens the per-product version arrays
  // into one timeline.

  import { catalogueState } from '../lib/catalogue-store.svelte';
  import { PRODUCTS } from '../lib/products';
  import { formatLocalDate } from '../lib/formatters';
  import type { ManifestVersionEntry } from '../lib/types';

  interface ActivityRow {
    product_label: string;
    version: ManifestVersionEntry;
  }

  const activity = $derived(buildActivity(catalogueState.manifest));

  function buildActivity(manifest: typeof catalogueState.manifest): ActivityRow[] {
    if (!manifest) return [];
    const rows: ActivityRow[] = [];
    for (const product of PRODUCTS) {
      const versions = manifest.jurisdictions?.[product.jurisdiction]?.[product.product]?.versions || [];
      for (const v of versions) {
        rows.push({ product_label: product.label, version: v });
      }
    }
    // Sort by the proximate-event timestamp — i.e. when the version
    // last transitioned, not when it is scheduled to go live. Recent
    // Activity is a what-happened feed; using effective_from would
    // show future-dated queued entries at the top while pushing
    // recently-dequeued or recently-superseded entries to the bottom.
    rows.sort((a, b) => {
      const aTime = proximateEventUtc(a.version);
      const bTime = proximateEventUtc(b.version);
      return (aTime ?? '') < (bTime ?? '') ? 1 : -1;
    });
    return rows.slice(0, 10);
  }

  // The proximate event for a manifest entry is the most recent
  // state transition recorded on the entry. Each status has its own
  // canonical timestamp:
  //   dequeued      → dequeued_at  (cancelled, terminal)
  //   published_now → published_now_at  (fast-forwarded; can be
  //                    in_effect or superseded by now)
  //   in_effect     → effective_from.utc  (became live)
  //   superseded    → superseded_at if present, else effective_from
  //   approved      → approved_at  (approved + queued, not yet live)
  //   proposed /
  //   under_review  → approved_at if recorded (rare), else
  //                    effective_from as a last resort
  function proximateEventUtc(v: ManifestVersionEntry): string | undefined {
    if (v.status === 'dequeued' && v.dequeued_at) return v.dequeued_at;
    if (v.published_now_at) return v.published_now_at;
    if (v.status === 'in_effect') return v.effective_from?.utc;
    if (v.status === 'superseded') {
      const superseded = typeof v.superseded_at === 'object'
        ? v.superseded_at?.utc
        : (v.superseded_at as string | undefined);
      return superseded ?? v.effective_from?.utc;
    }
    const approvedAt = typeof v.approved_at === 'object'
      ? v.approved_at?.utc
      : (v.approved_at as string | undefined);
    return approvedAt ?? v.effective_from?.utc;
  }

  // Localised display date for the proximate event. UTC instants are
  // ISO strings; non-UTC fields (approved_at as a 4-part timestamp)
  // resolve via the same helper. Falls back to effective_from.local
  // when no proximate event is available so the cell never renders
  // empty.
  function proximateEventLocal(v: ManifestVersionEntry): string {
    if (v.status === 'dequeued' && v.dequeued_at) return v.dequeued_at;
    if (v.published_now_at) return v.published_now_at;
    if (v.status === 'in_effect') return v.effective_from?.local ?? v.effective_from?.utc ?? '';
    if (v.status === 'superseded') {
      if (typeof v.superseded_at === 'object' && v.superseded_at?.local) return v.superseded_at.local;
      if (typeof v.superseded_at === 'string') return v.superseded_at;
      return v.effective_from?.local ?? '';
    }
    if (typeof v.approved_at === 'object' && v.approved_at?.local) return v.approved_at.local;
    if (typeof v.approved_at === 'string') return v.approved_at;
    return v.effective_from?.local ?? '';
  }

  // Verb describing what happened on the proximate-event date.
  // Pairs with the status label so the row reads as a sentence:
  // "19 May 2026 · AU Unsecured · ... · Dequeued · ...".
  function eventVerb(v: ManifestVersionEntry): string {
    if (v.status === 'dequeued') return 'Dequeued';
    if (v.published_now_at) return 'Published now';
    if (v.status === 'in_effect') return 'Went live';
    if (v.status === 'superseded') return 'Superseded';
    if (v.status === 'approved') return 'Approved · queued';
    if (v.status === 'under_review') return 'Under Review';
    if (v.status === 'proposed') return 'Proposed';
    return v.status;
  }
</script>

<section class="recent-activity">
  <h3 class="section-heading">Recent Activity</h3>
  {#if activity.length === 0}
    <p class="muted">No catalogue versions recorded yet.</p>
  {:else}
    <ul class="recent-activity__list">
      {#each activity as row (row.product_label + '-' + row.version.catalogue_version)}
        <li>
          <span class="muted recent-activity__date">{formatLocalDate(proximateEventLocal(row.version))}</span>
          <span class="recent-activity__product">{row.product_label}</span>
          <span>{row.version.change_summary ?? row.version.catalogue_version}</span>
          <span class="muted">{eventVerb(row.version)}</span>
          {#if row.version.status === 'dequeued' && row.version.dequeued_by}
            <span class="muted">· Dequeued by {row.version.dequeued_by}</span>
          {:else if row.version.approved_by}
            <span class="muted">· Approved by {row.version.approved_by}</span>
          {/if}
        </li>
      {/each}
    </ul>
  {/if}
</section>

<style>
  .recent-activity {
    margin-top: var(--space-6);
  }
  .recent-activity__list {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }
  .recent-activity__list li {
    display: grid;
    grid-template-columns: 9rem 10rem 1fr auto auto;
    gap: var(--space-3);
    align-items: baseline;
    padding: var(--space-2) 0;
    border-bottom: 1px solid var(--surface-line-soft);
    font-size: var(--type-sm);
  }
  .recent-activity__date { font-family: var(--font-mono); }
  .recent-activity__product { font-weight: 500; }
</style>
